import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_URL, AuthService } from '@ribera-web/auth-data-access';
import { KITCHEN_API_URL } from './kitchen-api.token';
import { KdsEventDto } from './kitchen-api.types';

/** Aviso sintetico que se emite cada vez que el stream (re)conecta. */
export const STREAM_CONECTADO = 'CONECTADO';

export type KdsStreamEvento = KdsEventDto | { type: typeof STREAM_CONECTADO };

const REINTENTO_MAXIMO_MS = 30_000;

/**
 * Stream SSE del KDS (/stream).
 *
 * Se lee con fetch y no con EventSource porque EventSource no permite enviar
 * el header Authorization que exige el gateway. Si la conexion se cae, se
 * reintenta con espera creciente; al reconectar emite CONECTADO para que la
 * pantalla recargue lo que se haya perdido.
 */
@Injectable({ providedIn: 'root' })
export class KitchenStreamService {
  private readonly base = inject(KITCHEN_API_URL);
  private readonly gatewayUrl: string = inject(API_URL);
  private readonly auth = inject(AuthService);

  eventos(estacion: string | null): Observable<KdsStreamEvento> {
    return new Observable<KdsStreamEvento>((subscriber) => {
      let cerrado = false;
      let intentos = 0;
      let controller: AbortController | null = null;
      let reintento: ReturnType<typeof setTimeout> | undefined;

      const conectar = async (): Promise<void> => {
        controller = new AbortController();
        try {
          const token = this.auth.token();
          const respuesta = await fetch(this.url(estacion), {
            headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            signal: controller.signal,
          });
          if (!respuesta.ok || !respuesta.body) {
            throw new Error(`Stream de cocina respondio ${respuesta.status}`);
          }
          intentos = 0;
          subscriber.next({ type: STREAM_CONECTADO });
          await leer(respuesta.body, (evento) => subscriber.next(evento));
        } catch (error) {
          if (!cerrado) {
            console.warn('[Cocina] Se perdio el stream en vivo, reintentando', error);
          }
        }
        if (!cerrado) {
          const espera = Math.min(REINTENTO_MAXIMO_MS, 1000 * 2 ** intentos++);
          reintento = setTimeout(conectar, espera);
        }
      };

      void conectar();

      return () => {
        cerrado = true;
        controller?.abort();
        clearTimeout(reintento);
      };
    });
  }

  private url(estacion: string | null): string {
    // Igual que apiInterceptor: las rutas relativas van contra el gateway
    const base = this.base.startsWith('/') ? `${this.gatewayUrl}${this.base}` : this.base;
    return estacion ? `${base}/stream?station=${encodeURIComponent(estacion)}` : `${base}/stream`;
  }
}

/** Parte el texto SSE en eventos ("data: ..." separados por linea en blanco). */
async function leer(cuerpo: ReadableStream<Uint8Array>, emitir: (evento: KdsEventDto) => void): Promise<void> {
  const lector = cuerpo.getReader();
  const decodificador = new TextDecoder();
  let pendiente = '';
  for (;;) {
    const { value, done } = await lector.read();
    if (done) {
      return;
    }
    pendiente += decodificador.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    let fin = pendiente.indexOf('\n\n');
    while (fin >= 0) {
      const bloque = pendiente.slice(0, fin);
      pendiente = pendiente.slice(fin + 2);
      const datos = bloque
        .split('\n')
        .filter((linea) => linea.startsWith('data:'))
        .map((linea) => linea.slice(5).trimStart())
        .join('\n');
      if (datos) {
        try {
          emitir(JSON.parse(datos) as KdsEventDto);
        } catch {
          // Un evento mal formado no debe cortar el stream
        }
      }
      fin = pendiente.indexOf('\n\n');
    }
  }
}
